/**
 * IndividualEmploymentSection — §6 Employment matrix + §5 student-visa override
 * + visa-class employment constraints (BACKLOG-0762, Bill 2026-06-12).
 *
 * Port of SPFx `EmploymentSection.tsx` (Personal-group branch) plus the
 * Priority-1 override:
 *
 *   If `isStudentVisa === true` (citizenship !== US Citizen + stayPurpose ===
 *   Student), Employer field is FORCED to a required school datalist with
 *   label "School (student visa)". Wins over any employment-status-driven
 *   label/UX — EXCEPT for dependent classes (F-2 / M-2), where the
 *   class-constraint lock below wins (the dependent isn't the student; the
 *   school-employer ceremony targets the F-1/M-1 principal).
 *
 * Employment-status matrix (priority 2):
 *   | Status     | Employer label | Employer UX         | Occupation UX            | NAICS        |
 *   |------------|----------------|---------------------|--------------------------|--------------|
 *   | Employed   | Employer       | Freeform            | SOC dropdown             | Auto from SOC|
 *   | Self Emp   | Employer       | Freeform            | SOC dropdown             | Auto from SOC|
 *   | Retired    | Employer       | Read-only "Retired" | SOC "Former Occupation"  | Auto from SOC|
 *   | Unemployed | Employer       | Read-only "Unempl." | SOC "Previous Occup."    | Auto from SOC|
 *   | Student    | School         | MO school datalist  | Read-only "Student"      | Freeform     |
 *   | Homemaker  | Employer       | Read-only "Homemakr"| Read-only "Homemaker"    | Freeform     |
 *   | Minor      | Employer       | Read-only "Minor"   | Read-only "Minor"        | Freeform     |
 *
 * Status-change side effects match §6 and the SPFx reference.
 *
 * Visa-class constraints (priority 0 — BACKLOG-0762, federal work-eligibility
 * rules as plain-language FSR guidance; class = person.visaType, extraction-fed
 * via normalizeVisaType — unknown class → NO constraints):
 *   - F-1 / M-1 / J-1: every status stays offered; selecting Employed shows a
 *     calm advisory (F-1's only when the employer isn't the school on file).
 *   - F-2 / M-2 (dependents): work prohibited → Status LIMITED to Homemaker /
 *     Minor (default by age: under 18 → Minor, else Homemaker); the existing
 *     AUTO_* sentinel rules lock/populate Employer + Occupation. Info line
 *     cites the prohibition + that ownership (incl. minor accounts) is
 *     unaffected (source: "Legacy Procedure - Non-Citizen ID Requirements.txt"
 *     — acceptable Work/Student classes "may have another number with them
 *     which represents that they are a dependent").
 *   - J-2 (dependent): Employed IS offered but soft-gated on a captured EAD
 *     (I-766) — until one is captured the requirement renders and
 *     `j2EadGatePending` keeps the Work/School tab incomplete. Never blocks.
 *
 * Core Director destinations (Bill 2026-06-12, sevis-school-catalog-runbook §0):
 *   F141 Employer   = 15 chars  — when Status=Student the CD value comes from
 *                                 the SEVIS catalog's server-computed
 *                                 `f141Employer` (extra.f141Employer), NOT a
 *                                 client-side truncation of this field.
 *   F313 Occupation = 30 chars  — 'Student' constant on the student path.
 */

import { useEffect } from 'react';
import type { RaoPerson } from '../../../types';
import {
  EMPLOYMENT_STATUSES,
  AUTO_EMPLOYER_STATUSES,
  AUTO_OCCUPATION_STATUSES,
  MO_SCHOOLS,
  SOC_OCCUPATIONS,
  SOC_OCCUPATIONS_BY_LABEL,
  socOccupationLabel,
  visaClassEmploymentRule,
  type VisaClassEmploymentRule,
  type EmploymentStatus,
} from './individual-config';
import { readAttachedDocs } from '../shared/attachedDocs';

// -----------------------------------------------------------------------------
// Styles
// -----------------------------------------------------------------------------

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--ink-600)',
  display: 'block',
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 4,
  border: '1px solid var(--ink-200)',
  fontSize: 14,
  boxSizing: 'border-box',
};

const readOnlyStyle: React.CSSProperties = {
  ...inputStyle,
  background: 'var(--field-readonly-bg, #f5f5f5)',
  color: 'var(--ink-700)',
};

const requiredErrStyle: React.CSSProperties = {
  ...inputStyle,
  borderColor: 'var(--pcb-red, #b10505)',
};

function changedFieldStyle(base: React.CSSProperties, changed: boolean): React.CSSProperties {
  return changed
    ? { ...base, borderLeft: '3px solid var(--pcb-blue, #2968ad)', background: 'var(--field-changed-bg, #f0f7ff)', color: 'var(--ink-900, #1d1d1f)' }
    : base;
}

/** Calm neutral advisory line (visa-class guidance — BACKLOG-0762). */
const advisoryInfoStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--ink-700)',
  background: 'var(--field-changed-bg, #f0f7ff)',
  border: '1px solid var(--info-line)',
  borderLeft: '3px solid var(--pcb-blue, #2968ad)',
  borderRadius: 4,
  padding: '8px 10px',
  lineHeight: 1.5,
};

/** Amber requirement line (J-2 EAD soft gate pending). */
const advisoryWarnStyle: React.CSSProperties = {
  ...advisoryInfoStyle,
  background: 'var(--warn-bg)',
  border: '1px solid var(--warn-line)',
  borderLeft: '3px solid var(--warn-line)',
};

// -----------------------------------------------------------------------------
// Visa-class helpers (BACKLOG-0762) — exported so IndividualReviewBody's
// Work/School tab-pill derivation can honor the J-2 soft gate.
// -----------------------------------------------------------------------------

/**
 * Resolve the visa-class employment rule for a person. Class source =
 * person.visaType (top-level, extraction-fed) with the Origin-tab mirror
 * person.extra.visaType as fallback. Unknown/absent → undefined (no
 * constraints — never guess).
 */
export function personVisaClassEmploymentRule(person: RaoPerson): VisaClassEmploymentRule | undefined {
  const extra = (person.extra ?? {}) as Record<string, unknown>;
  const mirrored = typeof extra.visaType === 'string' ? extra.visaType : undefined;
  return visaClassEmploymentRule(person.visaType ?? mirrored);
}

/**
 * True when an EAD (I-766) has been captured for this person — any of:
 * an AttachedDoc in the canonical 'EAD' slot (captureRouting DOC_TYPE_OPTIONS
 * slot = tileIdToCanonicalDocCode('ead')), the EAD extract fields the
 * /v3/nra/scan 'ead' case writes (extra.eadANumber / eadCategory / eadExpDt),
 * or a non-empty per-doc extract under extra.extracts.ead.
 */
export function hasCapturedEad(person: RaoPerson): boolean {
  if (readAttachedDocs(person).some((d) => d.docCode === 'EAD')) return true;
  const extra = (person.extra ?? {}) as Record<string, unknown>;
  if (extra.eadANumber || extra.eadCategory || extra.eadExpDt) return true;
  const extracts = extra.extracts;
  if (extracts && typeof extracts === 'object') {
    const ead = (extracts as Record<string, unknown>).ead;
    if (ead && typeof ead === 'object' && Object.keys(ead as object).length > 0) return true;
  }
  return false;
}

/**
 * J-2 soft gate — Status = Employed on a class that requires a captured EAD,
 * and no EAD captured yet. Consumed by IndividualReviewBody to keep the
 * Work/School tab pill on ⚠ (incomplete) — soft gate only, never a block.
 */
export function j2EadGatePending(person: RaoPerson): boolean {
  const rule = personVisaClassEmploymentRule(person);
  return !!rule?.employedRequiresEad
    && (person.employmentStatus ?? '') === 'Employed'
    && !hasCapturedEad(person);
}

/** Age in whole years from an ISO DOB; null when missing/unparseable. */
function ageFromDob(dob: string | null | undefined): number | null {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age;
}

/**
 * Default status for a status-limited (dependent) class: 'by-age' picks
 * Minor under 18, else Homemaker; unknown DOB → Homemaker (age not provably
 * under 18 — FSR corrects via the two-option dropdown).
 */
function defaultDependentStatus(rule: VisaClassEmploymentRule, dob: string | undefined): EmploymentStatus {
  if (rule.defaultStatus && rule.defaultStatus !== 'by-age') return rule.defaultStatus;
  if (rule.defaultStatus === 'by-age') {
    const age = ageFromDob(dob);
    return age !== null && age < 18 ? 'Minor' : 'Homemaker';
  }
  return rule.allowedStatuses?.[0] ?? 'Homemaker';
}

/** Loose name compare for "employer is the school on file" (F-1 advisory). */
function normLoose(s: string | undefined | null): string {
  return (s ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
}

/** True when DOB indicates the person is under 18. "Minor" status is derived
 *  purely from this — it is never a user-selectable employment status. */
function isMinorByDob(dob: string | undefined): boolean {
  if (!dob) return false;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dob);
  if (!m) return false;
  const birth = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(birth.getTime())) return false;
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const md = now.getMonth() - birth.getMonth();
  if (md < 0 || (md === 0 && now.getDate() < birth.getDate())) age -= 1;
  return age >= 0 && age < 18;
}

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

export interface IndividualEmploymentSectionProps {
  person: RaoPerson;
  onChange: (patch: Partial<RaoPerson>) => void;
  isChanged?: (field: string) => boolean;
  /** When true (from CitizenshipSection), Employer is forced to MO-school datalist. */
  isStudentVisa?: boolean;
  readOnly?: boolean;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function IndividualEmploymentSection({
  person,
  onChange,
  isChanged,
  isStudentVisa = false,
  readOnly = false,
}: IndividualEmploymentSectionProps): JSX.Element {
  const ic = isChanged ?? ((_: string) => false);
  const status = person.employmentStatus ?? '';

  const isAutoEmployer = AUTO_EMPLOYER_STATUSES.has(status) && status !== 'Student';
  const isAutoOcc = AUTO_OCCUPATION_STATUSES.has(status);

  // ── Visa-class constraints (BACKLOG-0762) ──────────────────────────────
  // Class = person.visaType (extraction-fed, normalizeVisaType'd) — unknown
  // class resolves NO rule, so legacy behavior is untouched.
  const classRule = personVisaClassEmploymentRule(person);
  /** Dependent classes (F-2/M-2): Status limited to Homemaker / Minor. */
  const dependentLock = !!classRule?.allowedStatuses;
  const personIsMinor = isMinorByDob(person.dob);
  // 'Minor' is DOB-derived, never user-selectable: hide it from the dropdown
  // unless the person actually is a minor (in which case it's auto-locked below).
  const statusOptions: ReadonlyArray<string> = classRule?.allowedStatuses
    ?? (personIsMinor ? EMPLOYMENT_STATUSES : EMPLOYMENT_STATUSES.filter((s) => s !== 'Minor'));
  const eadCaptured = hasCapturedEad(person);
  // Student-visa employer override is SUPPRESSED for dependent classes — the
  // dependent isn't the student; school-as-employer targets the F-1/M-1
  // principal (header doc-comment, BACKLOG-0762).
  const studentVisaOverride = isStudentVisa && !dependentLock;

  // Student-visa override wins over status-matrix employer UX.
  const employerLabel = studentVisaOverride
    ? 'School (student visa)'
    : status === 'Student'
      ? 'School'
      : 'Employer';
  const occupationLabel =
    status === 'Unemployed' ? 'Previous Occupation'
      : status === 'Retired' ? 'Former Occupation'
        : 'Occupation';

  // Occupation UX: SOC dropdown for Employed / Self-Employed / Retired / Unemployed;
  // read-only for Minor / Homemaker / Student; freeform fallback otherwise.
  const showSocDropdown =
    !isAutoOcc &&
    status !== 'Student' &&
    (status === 'Employed' || status === 'Self Employed' || status === 'Retired' || status === 'Unemployed');

  function handleStatusChange(val: string): void {
    // Always clear NAICS on a status change — NAICS is occupation-derived, so a
    // stale code (e.g. Agriculture/Farm 11) must not persist when switching to
    // Homemaker/Retired/etc. Employed/Self-Employed repopulate it when the
    // officer picks a new occupation. (Fixes stranded NAICS on status change.)
    const patch: Partial<RaoPerson> = { employmentStatus: val, naicsCode: '' };
    if (val === 'Minor') {
      patch.employer = 'Minor';
      patch.occupation = 'Minor';
    } else if (val === 'Homemaker') {
      patch.employer = 'Homemaker';
      patch.occupation = 'Homemaker';
    } else if (val === 'Retired') {
      patch.employer = 'Retired';
      patch.occupation = '';
    } else if (val === 'Unemployed') {
      patch.employer = 'Unemployed';
    } else if (val === 'Student') {
      // NRA student path (Bill 2026-06-12): Occupation = the constant
      // 'Student' → Core Director F313 Occupation (30 chars); Employer (→ CD
      // F141, 15 chars) is the school — auto-filled by the I-20 capture +
      // SEVIS catalog lookup (captureRouting i20 case) or picked below.
      patch.occupation = 'Student';
    } else if (val === 'Employed' || val === 'Self Employed') {
      // Clear any auto-populated sentinels so the officer can type fresh.
      if (isAutoEmployer) patch.employer = '';
      if (isAutoOcc) patch.occupation = '';
    }
    onChange(patch);
  }

  // ── Dependent-class default (BACKLOG-0762) ─────────────────────────────
  // F-2 / M-2: when the stored status isn't one of the two offered options
  // (fresh add, or a captured dependent visa stamp just narrowed the class),
  // apply the age-based default through handleStatusChange so the existing
  // sentinel rules lock/populate Employer + Occupation in the same patch.
  useEffect(() => {
    if (readOnly) return;
    if (!classRule?.allowedStatuses) return;
    if (classRule.allowedStatuses.includes(status as EmploymentStatus)) return;
    handleStatusChange(defaultDependentStatus(classRule, person.dob));
    // handleStatusChange/onChange identities change per render — keying on the
    // semantic inputs (class rule + current status + dob) is the loop-safe set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classRule, status, person.dob, readOnly]);

  // ── Minor is DOB-derived, never user-selectable ───────────────────────
  // When DOB indicates a minor, auto-lock status to 'Minor'. When the person
  // is NOT a minor but status is stale 'Minor' (e.g. DOB corrected), reset so
  // the officer picks a real status (the dropdown no longer offers 'Minor').
  useEffect(() => {
    if (readOnly) return;
    if (personIsMinor && status !== 'Minor') {
      handleStatusChange('Minor');
    } else if (!personIsMinor && status === 'Minor') {
      handleStatusChange('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personIsMinor, status, readOnly]);

  // ── Sentinel self-heal for prefilled statuses ─────────────────────────
  // (Bill import walk 2026-07-10, record 7592) — online-app imports (and any
  // other prefill) can arrive with employmentStatus already set (e.g.
  // 'Retired') while Employer/Occupation are empty, because the sentinel
  // population above lives ONLY in handleStatusChange (onChange). The result
  // was an EMPTY read-only Employer box until the FSR re-selected the status.
  // Apply the same sentinel patch when the target field is BLANK — it never
  // overwrites an officer-entered or imported value (fields are read-only
  // under these statuses anyway).
  useEffect(() => {
    if (readOnly) return;
    if (!status) return;
    const patch: Partial<RaoPerson> = {};
    if (AUTO_EMPLOYER_STATUSES.has(status) && !(person.employer ?? '').trim()) {
      patch.employer = status;
    }
    if (AUTO_OCCUPATION_STATUSES.has(status) && !(person.occupation ?? '').trim()) {
      patch.occupation = status;
    }
    if (status === 'Student' && !(person.occupation ?? '').trim()) {
      patch.occupation = 'Student';
    }
    if (Object.keys(patch).length > 0) onChange(patch);
    // onChange identity changes per render; keying on the semantic inputs is
    // the loop-safe set (same pattern as the effects above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, person.employer, person.occupation, readOnly]);

  // ── Class advisories (BACKLOG-0762) ────────────────────────────────────
  // F-1: advisory only when the stated employer isn't the school on file
  // (extra.schoolName from the I-20 extract · extra.f141Employer /
  // extra.schoolCommonName15 from the SEVIS catalog lookup). No school on
  // file → advisory shows (on-campus work can't be corroborated yet).
  const extraRec = (person.extra ?? {}) as Record<string, unknown>;
  const schoolNamesOnFile = [extraRec.schoolName, extraRec.f141Employer, extraRec.schoolCommonName15, extraRec.schoolParentName]
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    .map(normLoose);
  const employerIsSchoolOnFile =
    schoolNamesOnFile.length > 0 && schoolNamesOnFile.includes(normLoose(person.employer));
  const showEmployedAdvisory =
    !!classRule?.employedAdvisory
    && status === 'Employed'
    && (!classRule.advisoryOnlyWhenEmployerNotSchool || !employerIsSchoolOnFile)
    // J-2: the advisory IS the EAD requirement — clears once an EAD is captured.
    && (!classRule.employedRequiresEad || !eadCaptured);
  /** J-2 gate pending → amber requirement styling + incomplete tab pill. */
  const eadGatePending = !!classRule?.employedRequiresEad && status === 'Employed' && !eadCaptured;

  function handleOccupationSelect(occ: string): void {
    const match = SOC_OCCUPATIONS.find((o) => o.occupation === occ);
    const patch: Partial<RaoPerson> = { occupation: occ };
    if (match) patch.naicsCode = match.naics;
    onChange(patch);
  }

  const employerIsRequired = studentVisaOverride;
  const employerErr = employerIsRequired && !person.employer;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
        {/* Status dropdown — option set is class-aware (BACKLOG-0762):
            F-2 / M-2 dependents see ONLY Homemaker / Minor ("Employed"/
            "Student" not offered — work prohibited for these classes). */}
        <div>
          <label style={labelStyle} htmlFor="rao-ind-empstat">Status</label>
          <select
            id="rao-ind-empstat"
            value={status}
            onChange={(e) => handleStatusChange(e.currentTarget.value)}
            disabled={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('employmentStatus'))}
            title={dependentLock
              ? 'Employment Status — limited for dependent visa holders (work not permitted)'
              : 'Employment Status'}
          >
            <option value="">Select...</option>
            {statusOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        {/* Employer — Student / student-visa gets school datalist, auto-statuses are read-only */}
        <div>
          <label style={labelStyle} htmlFor="rao-ind-employer">
            {employerLabel}
            {employerIsRequired && <span style={{ color: 'var(--pcb-red, #b10505)' }}> *</span>}
          </label>
          {(status === 'Student' || studentVisaOverride) ? (
            <>
              {/* NRA smoke test (Bill 2026-06-12) — the school field must be
                  editable like its peers so the FSR can correct the captured
                  value (e.g. to the I-20's extracted "University of
                  Missouri"). The old maxLength=15 cap blocked typing past 15
                  characters, which read as a locked field; raised to 60
                  (datalist + freeform both allowed).

                  Core Director F141 Employer = 15 chars (Bill 2026-06-12).
                  This DISPLAY field stays 60-char editable; the ≤15 CD value
                  is the SEVIS catalog's server-computed `f141Employer`
                  (stored at extra.f141Employer by the captureRouting i20
                  lookup — commonName15 ?? word-boundary truncation). Commit
                  side reads that, never truncates this field blindly. */}
              <input
                id="rao-ind-employer"
                list="rao-ind-school-list"
                value={person.employer ?? ''}
                onChange={(e) => onChange({ employer: e.currentTarget.value })}
                readOnly={readOnly}
                style={changedFieldStyle(
                  readOnly ? readOnlyStyle : employerErr ? requiredErrStyle : inputStyle,
                  ic('employer'),
                )}
                title={`${employerLabel} — type freely or pick from the list`}
                maxLength={60}
                placeholder="Type or select a school..."
              />
              <datalist id="rao-ind-school-list">
                {MO_SCHOOLS.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
              {employerErr && (
                <div style={{ fontSize: 10, color: 'var(--pcb-red, #b10505)', marginTop: 2 }}>
                  Required for student-visa applicants
                </div>
              )}
            </>
          ) : (
            <input
              id="rao-ind-employer"
              value={person.employer ?? ''}
              onChange={(e) => onChange({ employer: e.currentTarget.value })}
              style={changedFieldStyle(isAutoEmployer || readOnly ? readOnlyStyle : inputStyle, ic('employer'))}
              readOnly={isAutoEmployer || readOnly}
              title={employerLabel}
              maxLength={60}
            />
          )}
        </div>

        {/* Occupation — SOC dropdown / read-only / freeform based on status */}
        <div>
          <label style={labelStyle} htmlFor="rao-ind-occupation">{occupationLabel}</label>
          {showSocDropdown ? (
            <select
              id="rao-ind-occupation"
              value={person.occupation ?? ''}
              onChange={(e) => handleOccupationSelect(e.currentTarget.value)}
              disabled={readOnly}
              style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('occupation'))}
              title={occupationLabel}
            >
              <option value="">Select occupation...</option>
              {/* A–Z by the displayed label (BACKLOG-1779); values unchanged. */}
              {SOC_OCCUPATIONS_BY_LABEL.map((o) => (
                <option key={o.occupation} value={o.occupation}>
                  {socOccupationLabel(o)}
                </option>
              ))}
            </select>
          ) : (
            /* Core Director F313 Occupation = 30 chars (Bill 2026-06-12,
               sevis-school-catalog-runbook §0) — maxLength matches the CD
               width. Student path writes the constant 'Student' here via
               handleStatusChange / the captureRouting i20 case. */
            <input
              id="rao-ind-occupation"
              value={person.occupation ?? ''}
              onChange={(e) => onChange({ occupation: e.currentTarget.value })}
              style={changedFieldStyle(isAutoOcc || readOnly ? readOnlyStyle : inputStyle, ic('occupation'))}
              readOnly={isAutoOcc || readOnly}
              title={occupationLabel}
              maxLength={30}
            />
          )}
        </div>

        {/* NAICS — auto-populated when SOC dropdown drove the pick; freeform otherwise */}
        <div>
          <label style={labelStyle} htmlFor="rao-ind-naics">NAICS code</label>
          <input
            id="rao-ind-naics"
            value={person.naicsCode ?? ''}
            onChange={(e) => onChange({ naicsCode: e.currentTarget.value })}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('naicsCode'))}
            title="NAICS code"
            maxLength={6}
          />
        </div>
      </div>

      {/* ── Visa-class guidance (BACKLOG-0762) — plain-language FSR lines.
          Source for the dependent prohibition + ownership note: "Legacy
          Procedure - Non-Citizen ID Requirements.txt" (acceptable Work/
          Student classes "may have another number with them which represents
          that they are a dependent" — dependent visa holders may be owners/
          joint owners, incl. minor accounts). ── */}
      {classRule?.infoLine && (
        <div style={advisoryInfoStyle} role="note">
          {classRule.infoLine}
        </div>
      )}
      {showEmployedAdvisory && classRule?.employedAdvisory && (
        <div
          style={eadGatePending ? advisoryWarnStyle : advisoryInfoStyle}
          role={eadGatePending ? 'alert' : 'note'}
        >
          {classRule.employedAdvisory}
          {eadGatePending && (
            <div style={{ marginTop: 4, fontSize: 11, color: 'var(--ink-600)' }}>
              The Work / School section stays incomplete until the EAD is
              captured (Documents to capture → Employment Authorization).
              Nothing is blocked — capture can happen any time.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
